// live-route.ts — "estou numa rota de TV ao vivo?" nos DOIS mundos (web e empacotado).
//
// PROBLEMA (bug de ÁUDIO na TV, achado na LG real via CDP em 2026-07-14):
// O route guard do LivePlayerProvider testava os padrões de rota (`/^\/live/`,
// `/^\/player\//`) contra `window.location.pathname` CRU. No web isso funciona:
// o app é servido da raiz do host, então o pathname JÁ É a rota (`/live/123`).
//
// Mas no empacotado (.ipk/.wgt) o WebView carrega o index.html via `file://` a
// partir do diretório de instalação, e o router usa um `basename` derivado desse
// caminho (ver `router-basename.ts`). Resultado: navegar pra `/live` deixa o
// pathname assim:
//
//   /media/developer/apps/usr/palm/applications/com.swipetv.tv/index.html/live
//
// que NÃO casa com `/^\/live/`. Ou seja: na TV, `inLive` era SEMPRE `false`.
// Consequência medida no aparelho: `doSuspend()` disparava no mount e em TODA
// navegação — inclusive ao ENTRAR na live — pausando o `<video>` singleton,
// setando `video.muted = true` e agendando o GC agressivo. Efeito pro usuário:
// TV ao vivo (preview e player) sem áudio e sem imagem. No navegador nada disso
// acontecia, porque lá o pathname casa. Bug 100% do mundo empacotado.
//
// SOLUÇÃO (genérica por CAPABILITY, não por marca/OS):
// Antes de testar os padrões, tiramos o `basename` do pathname — o MESMO basename
// que o react-router usa. No web o basename é '/' e a função é um no-op (nada
// regride). No empacotado, o basename é o pathname do index.html e sai fora,
// sobrando a rota lógica (`/live`). Não existe `if (webOS)` em lugar nenhum:
// o basename vem do runtime, então vale pra webOS, Tizen, Android TV e web.
//
// Funções puras (sem DOM) — testáveis direto.

/** Rotas onde o player de live deve continuar VIVO (com áudio). */
export const LIVE_ROUTE_PATTERNS = [/^\/live(\/|$)/, /^\/player\//]

/** Subconjunto de `window.location` que interessa (facilita testar sem DOM). */
export interface LocationLike {
  protocol: string
  pathname: string
}

/**
 * Basename do app — IDEMPOTENTE, isto é, independente da rota atual.
 *
 * POR QUE NÃO USAR `deriveRouterBasename` DIRETO AQUI:
 * aquela função foi feita pra rodar UMA VEZ, no boot, quando o pathname ainda é
 * o do index.html. Se você chamar ela DEPOIS de navegar, no mundo file:// ela
 * devolve o pathname atual INTEIRO — já com a rota grudada
 * (`.../index.html/live`). O guard usaria basename === pathname, o "resto" viraria
 * '/', e ele concluiria de novo "não estou na live" — o MESMO bug, disfarçado.
 * (Aconteceu: o primeiro fix passou porque o desmute do play mascarava, mas o
 * preview do Hero continuava sendo suspenso — pego na LG real por CDP.)
 *
 * Aqui cortamos no documento (`/index.html`), então o resultado é o mesmo ANTES
 * e DEPOIS de qualquer navegação. Genérico: vale pra webOS, Tizen e Android TV
 * (todos empacotam com index.html) e é no-op no web.
 */
export function computeAppBasename(loc: LocationLike): string {
  const protocol = (loc.protocol || '').toLowerCase()
  const path = loc.pathname || '/'

  // Web (servido por host): rotas absolutas a partir da raiz.
  if (protocol === 'http:' || protocol === 'https:') return '/'

  // Empacotado: o documento é o index.html. Corta tudo DEPOIS dele.
  const marker = '/index.html'
  const i = path.indexOf(marker)
  if (i >= 0) return path.slice(0, i + marker.length)

  // Fallback (documento sem index.html explícito): usa o pathname como veio.
  return path
}

/** Cache do basename (idempotente, mas evita recomputar a cada navegação). */
let cachedBasename: string | null = null

/** Basename do runtime. Sem `window` (SSR/teste), devolve '/' (mundo web). */
export function getAppBasename(): string {
  if (cachedBasename !== null) return cachedBasename
  if (typeof window === 'undefined' || !window.location) return '/'
  cachedBasename = computeAppBasename(window.location)
  return cachedBasename
}

/** Só pra teste: zera o cache do basename entre casos. */
export function resetAppBasenameCache(): void {
  cachedBasename = null
}

/**
 * Remove o `basename` do pathname, devolvendo a rota lógica do app.
 *
 * Exemplos:
 *   toAppPath('/live/123', '/')                        -> '/live/123'   (web)
 *   toAppPath('/a/b/index.html/live', '/a/b/index.html') -> '/live'     (empacotado)
 *   toAppPath('/a/b/index.html', '/a/b/index.html')      -> '/'         (carga inicial)
 *
 * Robustez: se o pathname não começar com o basename (estado inesperado),
 * devolve o pathname como veio — pior caso é o comportamento antigo, nunca um
 * throw. Sempre devolve algo começando com '/'.
 */
export function toAppPath(pathname: string, basename: string): string {
  const path = pathname || '/'
  const base = basename || '/'

  // Web (ou basename ausente): o pathname já é a rota.
  if (base === '/') return path

  // Carga inicial do empacotado: pathname === basename → rota raiz.
  if (path === base) return '/'

  if (path.startsWith(base)) {
    const rest = path.slice(base.length)
    // `rest` já começa com '/' (ex: '/live'); se vier vazio, é a raiz.
    if (rest === '') return '/'
    return rest.startsWith('/') ? rest : `/${rest}`
  }

  // Não casou com o basename — devolve como veio (fallback conservador).
  return path
}

/**
 * True quando a rota lógica (pathname SEM o basename) é uma rota de live.
 *
 * É esta função que decide se o `<video>` singleton da live continua tocando
 * (com áudio) ou se é suspenso. Passar o basename é obrigatório justamente pra
 * não repetir o bug do empacotado.
 */
export function isLiveRoute(pathname: string, basename: string): boolean {
  const appPath = toAppPath(pathname, basename)
  return LIVE_ROUTE_PATTERNS.some((re) => re.test(appPath))
}
